import { useAuth } from '@agentnet/auth';
import { Grid, Theme } from '@agentnet/components';
import {
  CellLink,
  CellMessage,
  CellStatus,
  Content,
  DashboardMetricCard,
  DataTable,
  PageHeader,
  pxToRem,
} from '@fluentsms/agentnet-web-components';
import { createStyles, makeStyles } from '@material-ui/styles';
import {
  CellClickedEvent,
  ColDef,
  FilterChangedEvent,
  FilterModel,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  RowNode,
  RowSelectedEvent,
  ValueFormatterParams,
} from 'ag-grid-enterprise';
import { getFirmDetails } from 'api/onBehalfOf/api';
import { getFilesInProcessApi } from 'api/orderManagement/order-management-api';
import clsx from 'clsx';
import { ConversationDrawer } from 'features/orderManagement/ConversationDrawer';
import {
  getColorsByProcessingStatus,
  ProcessingStatus,
  ReasonCode,
  TeamAssignment,
} from 'features/orderManagement/enum';
import { fileListFilterParams, formatDate, numberValueFormatter } from 'features/orderManagement/fileListConfig';
import FileStatusIndicator from 'features/orderManagement/FileStatusIndicator';
import {
  FileEditAssignmentType,
  FileInProcessType,
  GetNotesActionLog,
  OrderType,
} from 'features/orderManagement/types';
import CalculatorRenderer from 'features/Remittance/CalculatorRenderer';
import useRatesAndFee from 'features/Remittance/useRatesAndFee';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import useAsync from 'hooks/useAsync';
import useSessionStorage from 'hooks/useSessionStorage';
import moment from 'moment';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import BottomAppBar from 'ui-kit/components/bottomAppBar/BottomAppBar';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import ContentContainer from 'ui-kit/components/utility/ContentContainer';
import { getFullName } from 'utilities/utilities';
import ReportsAndPayDrawer from '../ReportsAndPayDrawer';
import { FileEditAssignmentContainer } from './FileEditAssignmentContainer';

const colorsByStatus = getColorsByProcessingStatus();

export function FilesInProcessContent() {
  const { getAccessToken } = useAuth();
  const gridApiRef = useRef<GridApi<FileInProcessType> | null>(null);
  const { addGlobalMsg } = useGlobalMessages();
  const [filesInProcess, setFilesInProcess] = useState<Array<FileInProcessType>>([]);
  const [selectedFiles, setSelectedFiles] = useState<Array<FileInProcessType>>([]);
  const [currentTeam, setCurrentTeam] = useState<number | null>(null);
  const [displayedRowCount, setDisplayedRowCount] = useState<number>(0);
  const [editAssignmentDrawerOpen, setEditAssignmentDrawerOpen] = useState<boolean>(false);
  const [editAssignmentData, setEditAssignmentData] = useState<FileEditAssignmentType[]>([]);

  const [conversationFileNumber, setConversationFileNumber] = useState<string>();
  const [conversationPayload, setConversationPayload] = useState<GetNotesActionLog | undefined>(undefined);

  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { setUserFirm } = profileCtx;

  const [filesInProcessSessionColumnDefs, setFilesInProcessSessionColumnDefs] = useSessionStorage(
    'filesInProcessSessionColumnDefs',
  );
  const [filesInProcessSessionFilterModel, setFilesInProcessSessionFilterModel] = useSessionStorage(
    'filesInProcessSessionFilterModel',
  );

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      root: {
        width: '100%',
        marginTop: pxToRem(114),
      },
      selectedRows: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100% !important',
        fontSize: pxToRem(14),
        paddingTop: theme.spacing(2),
        paddingLeft: theme.spacing(3),
      },
      heading: {
        marginLeft: 0,
        marginRight: 0,
        marginBottom: 20,
      },
      contentWrap: {
        justifyContent: 'space-between',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100%)',
      },
      contentClass: {
        margin: '0 auto auto',
      },
      tableDefault: {
        fontFamily: theme.typography.fontFamily,
        '& .ag-header-cell-center .ag-header-cell-label': {
          justifyContent: 'center',
        },
        '& .ag-header-cell-right .ag-header-cell-label': {
          justifyContent: 'flex-end',
        },
      },
      paddingX: {
        paddingLeft: theme.spacing(0),
        paddingRight: theme.spacing(0),
        width: 'auto',
      },
      noRows: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 220,
        justifyContent: 'center',
      },
      personBackground: {
        backgroundColor: '#696d7033',
        borderRadius: 50,
      },
      tableHeight: {
        height: selectedFiles.length ? 'calc(100vh - 385px)' : 'calc(100vh - 340px)',
      },
    }),
  );
  const classes = useStyles();
  const history = useHistory();

  const headerCheckboxRenderer = () => {
    return <input type="checkbox" data-testid={'OrderManagement-FilesInProcess-AllCheckbox'} />;
  };

  const rowCheckboxRenderer = (params: { node: RowNode }) => {
    return <input type="checkbox" data-testid={`OrderManagement-FilesInProcess-Checkbox-${params.node.id}`} />;
  };

  const { drawerData, setDrawerData, openDrawer, setOpenDrawer, handleDrawerOpen, rateAndFeeExecute } = useRatesAndFee(
    profileCtx.userFirm?.firmId ?? '',
    'order-management',
  );

  const onGridReady = (params: GridReadyEvent<FileInProcessType>) => {
    gridApiRef.current = params.api;
    if (!filesInProcessSessionFilterModel) {
      // Remove (Blanks) from Reason Code column filter
      const defaultFilterModel: FilterModel = {
        reasonCodeColumn: {
          filterType: 'set',
          values: Object.values(ReasonCode),
        },
      };
      params.api.setFilterModel(defaultFilterModel);
    } else {
      params.api.setFilterModel(filesInProcessSessionFilterModel);
    }
  };

  const onFilterChanged = (e: FilterChangedEvent): void => {
    setDisplayedRowCount(getDisplayedRowCount());
    setFilesInProcessSessionFilterModel(e.api.getFilterModel());
  };

  const getDisplayedRowCount = (): number => {
    return gridApiRef.current?.getDisplayedRowCount() ?? -1;
  };

  const selectedFirmIdRef = useRef<number | null | undefined>(undefined);
  const hasSelectionRef = useRef<boolean>(false);
  const hasShownFirmWarningRef = useRef<boolean>(false);

  const onSelectionChanged = useCallback(() => {
    // Reset the warning flag when selection changes (allows showing warning again on next attempt)
    hasShownFirmWarningRef.current = false;
    const selectedRows = gridApiRef.current?.getSelectedRows() ?? [];
    setSelectedFiles(selectedRows);
    // Track whether we have any selection and the firm ID of the first selected row
    hasSelectionRef.current = selectedRows.length > 0;
    selectedFirmIdRef.current = selectedRows.length > 0 ? selectedRows[0].firmId : undefined;
  }, []);

  const onRowSelected = useCallback(
    (event: RowSelectedEvent<FileInProcessType>) => {
      // Only check when a row is being selected (not deselected)
      if (!event.node.isSelected() || !event.data) {
        return;
      }

      const currentFirmId = selectedFirmIdRef.current;
      const rowFirmId = event.data.firmId;

      // If there's already a selection and the new row has a different firmId, handle the conflict
      // Note: null/undefined firmId is treated as its own group (files with no firm can be selected together)
      if (hasSelectionRef.current && rowFirmId !== currentFirmId) {
        // If this is a "select all" action (header checkbox), deselect all instead of blocking
        // This allows users to clear their selection via the header checkbox
        if (
          event.source === 'uiSelectAll' ||
          event.source === 'uiSelectAllFiltered' ||
          event.source === 'uiSelectAllCurrentPage'
        ) {
          gridApiRef.current?.deselectAll();
          return;
        }

        // For individual row selections, deselect this row
        event.node.setSelected(false);

        // Show warning message only once per selection batch
        if (!hasShownFirmWarningRef.current) {
          hasShownFirmWarningRef.current = true;
          // Find the firm name from currently selected files for the message
          const selectedRows = gridApiRef.current?.getSelectedRows() ?? [];
          const firmName = selectedRows.find((f) => f.firmId === currentFirmId)?.firmName ?? 'selected firm';

          addGlobalMsg({
            message: `Cannot select files from different headquarters. Selected files are from headquarter "${firmName}".`,
            type: 'warning',
          });
        }
      }
    },
    [addGlobalMsg],
  );

  const netAmountTotal = selectedFiles?.reduce(
    (accumulator: number, obj: FileInProcessType) => accumulator + obj.reportedAmount,
    0,
  );

  const selectedFilesText = `Selecting ${selectedFiles?.length} file(s) for ${numberValueFormatter(
    netAmountTotal,
    true,
  )}`;

  const dateRenderer = (params: { value: string }) => {
    return <>{formatDate(params.value ?? '')}</>;
  };

  const defaultColDef: ColDef = {
    editable: false,
    filter: 'agTextColumnFilter',
    flex: 2,
    floatingFilter: true,
    resizable: true,
    sortable: true,
    suppressFloatingFilterButton: true,
    suppressHeaderFilterButton: true,
    suppressHeaderMenuButton: true,
  };

  const columnDefs: ColDef[] = [
    {
      colId: 'checkboxColumn',
      field: '',
      maxWidth: 40,
      filter: false,
      floatingFilter: false,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      checkboxSelection: true,
      showDisabledCheckboxes: true,
      pinned: 'left',
      headerComponent: headerCheckboxRenderer,
      cellRenderer: rowCheckboxRenderer,
      resizable: false,
      suppressMovable: true,
    },
    {
      colId: 'fileStatusIndicatorColumn',
      headerName: '',
      field: 'fileInfoStatus',
      minWidth: 50,
      maxWidth: 50,
      filter: false,
      floatingFilter: false,
      sortable: false,
      resizable: false,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <FileStatusIndicator fileInfoStatus={params.data?.fileInfoStatus} isReopened={params.data?.isReopened} />
        );
      },
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
    },
    {
      colId: 'assignedToGroupColumn',
      headerName: 'Team',
      field: 'assignedToGroup',
      minWidth: 100,
      maxWidth: 150,
      filter: 'agSetColumnFilter',
      tooltipField: 'assignedToGroup',
      suppressFloatingFilterButton: false,
    },
    {
      colId: 'assigneeColumn',
      headerName: 'Assignee',
      field: 'fullName',
      filter: 'agSetColumnFilter',
      minWidth: 144,
      maxWidth: 256,
      tooltipField: 'assignedTo',
    },
    {
      colId: 'statusColumn',
      headerName: 'Processing Status',
      field: 'status',
      filter: 'agSetColumnFilter',
      minWidth: 144,
      maxWidth: 256,
      tooltipField: 'status',
      cellRenderer: (params: ICellRendererParams) => {
        const status = params.data?.status ?? '';
        return <CellStatus status={status} colorsByStatus={colorsByStatus} />;
      },
      suppressFloatingFilterButton: false,
    },
    {
      colId: 'batchIdColumn',
      headerName: 'Order ID',
      field: 'batchId',
      minWidth: 120,
      maxWidth: 170,
      tooltipField: 'orderStatus',
      cellRenderer: CellLink,
      cellRendererParams: () => ({
        linkTemplate: 'order-management',
      }),
      onCellClicked: (event: CellClickedEvent) => {
        setFirmName(event.data.firmId);
        goToOrderDetails(event.data);
      },
      sort: 'desc',
    },
    {
      colId: 'propertyStateColumn',
      headerName: 'Property State',
      field: 'propertyState',
      minWidth: 125,
      maxWidth: 175,
      tooltipField: 'propertyState',
      cellStyle: { textAlign: 'center' },
    },
    {
      colId: 'firmNameColumn',
      headerName: 'Firm',
      field: 'firmName',
      minWidth: 256,
      tooltipField: 'firm',
    },
    {
      colId: 'fileNumberColumn',
      headerName: 'File Number',
      field: 'fileNumber',
      minWidth: 144,
      maxWidth: 384,
      tooltipField: 'fileNumber',
      cellRenderer: CellLink,
      // temporary open in new tab until we have a proper order management navigation menu
      cellRendererParams: (row: ValueFormatterParams) => ({
        linkTemplate: `/files/${row.data.fileId}/file-info`,
        openNewTab: true,
      }),
      onCellClicked: (event: CellClickedEvent) => {
        setFirmName(event.data.firmId);
      },
    },
    {
      colId: 'reasonCodeColumn',
      headerName: 'Reason Code',
      field: 'reasonCode',
      minWidth: 125,
      maxWidth: 256,
      filter: 'agSetColumnFilter',
      tooltipField: 'reasonCode',
      suppressFloatingFilterButton: false,
    },
    {
      colId: 'accountNumberColumn',
      headerName: 'Account Number',
      field: 'firmLocationJurisdictionId',
      minWidth: 144,
      tooltipField: 'firmLocationJurisdictionId',
    },
    {
      colId: 'orderDateColumn',
      headerName: 'Order Date',
      field: 'orderDate',
      minWidth: 120,
      maxWidth: 170,
      filterParams: fileListFilterParams,
      cellRenderer: dateRenderer,
      filter: 'agDateColumnFilter',
      tooltipField: 'orderDate',
      cellStyle: { textAlign: 'center' },
      headerClass: 'ag-header-cell-center',
    },
    {
      colId: 'lastUpdatedColumn',
      headerName: 'Last Note',
      field: 'lastUpdated',
      minWidth: 120,
      maxWidth: 170,
      filterParams: fileListFilterParams,
      cellRenderer: dateRenderer,
      filter: 'agDateColumnFilter',
      tooltipField: 'lastUpdated',
      cellStyle: { textAlign: 'center' },
      headerClass: 'ag-header-cell-center',
    },
    {
      colId: 'grossPremiumColumn',
      headerName: 'Gross Premium',
      field: 'grossPremium',
      minWidth: 135,
      maxWidth: 175,
      filter: 'agNumberColumnFilter',
      valueFormatter: numberValueFormatter,
      tooltipField: 'grossPremium',
      cellStyle: { textAlign: 'right' },
      headerClass: 'ag-header-cell-right',
    },
    {
      colId: 'netPremiumColumn',
      headerName: 'Net Premium',
      field: 'netPremium',
      minWidth: 135,
      maxWidth: 175,
      filter: 'agNumberColumnFilter',
      valueFormatter: numberValueFormatter,
      tooltipField: 'netPremium',
      cellStyle: { textAlign: 'right' },
      headerClass: 'ag-header-cell-right',
    },
    {
      colId: 'lockboxTicketNumberColumn',
      headerName: 'Lockbox/Ticket Number',
      field: 'remitBatchSource',
      minWidth: 180,
      maxWidth: 230,
      tooltipField: 'lockboxTicketNumber',
    },
    {
      colId: 'batchColumn',
      headerName: 'Batch',
      field: 'referenceText',
      minWidth: 105,
      maxWidth: 155,
      tooltipField: 'referenceText',
    },
    {
      colId: 'costCenterColumn',
      headerName: 'Cost Center',
      field: 'costCenter',
      minWidth: 115,
      maxWidth: 165,
      tooltipField: 'costCenter',
    },
    {
      colId: 'fileIdColumn',
      headerName: 'File Id',
      field: 'fileId',
      tooltipField: 'fileId',
      hide: true,
    },
    {
      colId: 'firmIdColumn',
      headerName: 'Firm Id',
      field: 'firmId',
      filter: 'agNumberColumnFilter',
      tooltipField: 'firmId',
      hide: true,
    },
    {
      colId: 'calculatorColumn',
      field: '',
      floatingFilter: false,
      sortable: false,
      cellRenderer: CalculatorRenderer,
      cellRendererParams: () => ({
        tabName: 'order-management',
        isRemitValid: true,
      }),
      onCellClicked: (event: CellClickedEvent) => {
        handleDrawerOpen(event, 'order-management');
      },
      maxWidth: 40,
      cellStyle: { display: 'flex', justifyContent: 'center', cursor: 'pointer', alignItems: 'center' },
      tooltipValueGetter: () => 'Open Rates and Fees',
      pinned: 'right',
    },
    {
      colId: 'notesColumn',
      field: '',
      floatingFilter: false,
      sortable: false,
      cellRenderer: CellMessage,
      onCellClicked: (event: CellClickedEvent<FileInProcessType>) => {
        try {
          const payload: GetNotesActionLog = {
            orderId: event.data?.batchId ?? 0,
            fileId: `${event.data?.fileId}`,
          };
          setConversationFileNumber(event.data?.fileNumber ?? '');
          setConversationPayload(payload);
        } catch (e) {
          if (e instanceof Error) {
            addGlobalMsg({
              message: e.message,
              type: 'error',
            });
          }
        }
      },
      minWidth: 40,
      maxWidth: 40,
      cellStyle: { display: 'flex', justifyContent: 'center', cursor: 'pointer', alignItems: 'center' },
      tooltipValueGetter: () => 'Open conversation panel',
      pinned: 'right',
    },
  ];

  const getColumnConfig = (): ColDef[] => {
    if (filesInProcessSessionColumnDefs) {
      return filesInProcessSessionColumnDefs.map((sessionColumn: ColDef) => {
        const colDef = columnDefs.find((c) => c.colId === sessionColumn.colId);
        if (colDef) {
          colDef.sort = sessionColumn.sort;
          return colDef;
        }
      });
    }
    return columnDefs;
  };

  const setFirmName = async (firmId: number) => {
    const token = await getAccessToken();
    const firmDetailsResults = await getFirmDetails(token, String(firmId));
    if (firmDetailsResults) {
      window.localStorage.setItem('userFirm', JSON.stringify(firmDetailsResults));
      setUserFirm && setUserFirm(firmDetailsResults);
    }
  };

  const goToOrderDetails = (row: FileInProcessType) => {
    const myOrder: OrderType = {
      firmId: row.firmId,
      lockboxId: row.remitBatchSourceId,
      orderId: row.batchId,
      orderDate: row.orderDate,
      referenceNumber: row.referenceText,
      checkCount: row.checkCount,
      reportedAmount: row.reportedAmount,
      targetTab: 'orderQueue',
      firm: {
        firmId: row.firmId,
        firmName: row.firmName,
        isActive: true,
      },
      lockboxNumber: {
        name: row.remitBatchSource,
        value: row.remitBatchSourceId,
      },
    };
    history.push('/order-management', { order: myOrder, from: '/order-management/files-in-process' });
  };

  const getFilesInProcess = async (): Promise<Array<FileInProcessType>> => {
    const token = await getAccessToken();
    const response = await getFilesInProcessApi(token);
    return response.result;
  };

  const {
    execute: executeGetFilesInProcess,
    status: executeGetFilesInProcessStatus,
    value: getFilesInProcessValue,
    errors: getFilesInProcessErrors,
  } = useAsync<Array<FileInProcessType>>(getFilesInProcess, false);

  const onTeamFilterClick = useCallback(
    (team: TeamAssignment): void => {
      if (gridApiRef.current) {
        const filterModel: FilterModel = gridApiRef.current.getFilterModel();
        const isTeamFilterActive = !!filterModel['assignedToGroupColumn'];

        const applyTeamFilter = async (team: TeamAssignment | null) => {
          const filter = Object.keys(TeamAssignment).find(
            (key) => TeamAssignment[key as keyof typeof TeamAssignment] === team,
          );
          await gridApiRef.current?.setColumnFilterModel('assignedToGroupColumn', {
            filterType: 'set',
            values: [filter],
          });
          gridApiRef.current?.onFilterChanged();
          setCurrentTeam(team);
        };

        if (isTeamFilterActive && currentTeam === team) {
          applyTeamFilter(null).catch(console.error);
          return;
        }

        applyTeamFilter(team).catch(console.error);
      }
    },
    [currentTeam],
  );

  let ifscCount = 0;
  let pfscCount = 0;
  let crsCount = 0;

  getFilesInProcessValue?.forEach((fileInProcess) => {
    switch (fileInProcess.assignedToGroup) {
      case 'IFSC':
        ifscCount++;
        break;
      case 'PFSC':
        pfscCount++;
        break;
      case 'CRS':
        crsCount++;
        break;
    }
    if (!fileInProcess.reasonCode && fileInProcess.reasonCodeId) {
      fileInProcess.reasonCode = ReasonCode[fileInProcess.reasonCodeId];
    }
    if (!fileInProcess.status && fileInProcess.statusId) {
      fileInProcess.status = ProcessingStatus[fileInProcess.statusId];
    }
  });

  const onEditAssignmentSaved = useCallback(() => {
    gridApiRef.current?.deselectAll();
  }, []);

  useEffect(() => {
    executeGetFilesInProcess();
  }, []);

  useEffect(() => {
    if (executeGetFilesInProcessStatus === 'success') {
      getFilesInProcessValue?.forEach((file) => {
        if ((file.orderDate?.length ?? 0) >= 10) {
          file.orderDate = file.orderDate?.substring(0, 10);
        }
        if (file.lastUpdated) {
          // Parse as UTC, then convert to local timezone so it matches the conversation notes drawer.
          const localDate = moment.utc(file.lastUpdated).local();
          file.lastUpdated = localDate.format('YYYY-MM-DD');
        }
        file.orderId = file.batchId;
        file.fullName = getFullName(file);
      });
      setDisplayedRowCount(getFilesInProcessValue?.length ?? 0);
      setFilesInProcess(getFilesInProcessValue ?? []);
    }
    if (executeGetFilesInProcessStatus === 'error') {
      getFilesInProcessErrors?.map((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [executeGetFilesInProcessStatus]);

  if (!getFilesInProcessValue) {
    return (
      <ContentContainer fullWidth>
        <Content>
          <div className={classes.noRows}>
            <LoadingSpinner status="pending" className="files-container--spinner-root" />
          </div>
        </Content>
      </ContentContainer>
    );
  }

  const renderBottomAppBar = () => {
    return (
      selectedFiles.length > 0 && (
        <BottomAppBar
          primaryActionLabel="Modify Assignment"
          primaryActionDataQa="order-mgmt-files-in-process-button-modify-assignment"
          disablePrimary={!selectedFiles.every((selectedFile) => selectedFile.firmId == selectedFiles[0].firmId)}
          cancelLabel="Clear All"
          cancelDataQa="order-mgmt-files-in-process-button-clear-all"
          onPrimaryClick={() => {
            for (const selectedFile of selectedFiles) {
              selectedFile.fullName ||= selectedFile.assignedTo && getFullName(selectedFile);
            }
            setEditAssignmentDrawerOpen(true);
            setEditAssignmentData([...selectedFiles]);
          }}
          omitPrimaryBorder={true}
          onCancel={() => gridApiRef?.current?.deselectAll()}
        />
      )
    );
  };

  return (
    <ContentContainer fullWidth>
      <Content>
        <div>
          <PageHeader className={classes.heading} title="Files In Process" />
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <DashboardMetricCard
                active={currentTeam === TeamAssignment.IFSC}
                dataTestId="order-mgmt-files-in-process-dashboard-metric-ifsc"
                heading="IFSC"
                status="Searching"
                value={ifscCount.toString()}
                onClick={() => onTeamFilterClick(TeamAssignment.IFSC)}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <DashboardMetricCard
                active={currentTeam === TeamAssignment.PFSC}
                dataTestId="order-mgmt-files-in-process-dashboard-metric-pfsc"
                heading="PFSC"
                status="Quality Review"
                value={pfscCount.toString()}
                onClick={() => onTeamFilterClick(TeamAssignment.PFSC)}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <DashboardMetricCard
                active={currentTeam === TeamAssignment.CRS}
                dataTestId="order-mgmt-files-in-process-dashboard-metric-crs"
                heading="CRS"
                status="Examining"
                value={crsCount.toString()}
                onClick={() => onTeamFilterClick(TeamAssignment.CRS)}
              />
            </Grid>
            <Grid item xs={12}>
              <div className={clsx('ag-theme-alpine', classes.tableDefault, classes.paddingX, classes.tableHeight)}>
                <DataTable
                  rowData={filesInProcess}
                  columnDefs={getColumnConfig()}
                  defaultColDef={defaultColDef}
                  onGridReady={onGridReady}
                  onFilterChanged={onFilterChanged}
                  onSelectionChanged={onSelectionChanged}
                  onRowSelected={onRowSelected}
                  gridOptions={{ suppressRowClickSelection: true, rowSelection: 'multiple' }}
                  animateRows={true}
                  noRowsOverlayComponent={'customOverlayNoRows'}
                  onGridColumnsChanged={(e) => {
                    setFilesInProcessSessionColumnDefs(e.api.getColumnDefs() || []);
                  }}
                  onColumnMoved={(e) => {
                    setFilesInProcessSessionColumnDefs(e.api.getColumnDefs() || []);
                  }}
                  onSortChanged={(e) => {
                    setFilesInProcessSessionColumnDefs(e.api.getColumnDefs() || []);
                  }}
                />
              </div>
            </Grid>
          </Grid>
          <div className={clsx(classes.paddingX, classes.selectedRows)}>
            <span>
              Showing {displayedRowCount >= 0 ? displayedRowCount : getFilesInProcessValue.length} of{' '}
              {getFilesInProcessValue.length} {'files.'}
            </span>
            {selectedFiles?.length ? selectedFilesText : null}
          </div>
          {renderBottomAppBar()}
          {openDrawer && (
            <ReportsAndPayDrawer
              openDrawer={openDrawer}
              setOpenDrawer={setOpenDrawer}
              drawerData={drawerData}
              setDrawerData={setDrawerData}
              rateAndFeeExecute={rateAndFeeExecute}
            />
          )}
          {editAssignmentDrawerOpen && (
            <FileEditAssignmentContainer
              open={editAssignmentDrawerOpen}
              setOpen={setEditAssignmentDrawerOpen}
              data={editAssignmentData}
              onSaved={onEditAssignmentSaved}
            />
          )}
          {conversationFileNumber && conversationPayload && (
            <ConversationDrawer
              conversationPayload={conversationPayload}
              conversationFileNumber={conversationFileNumber}
              onClose={() => setConversationPayload(undefined)}
              isDashboardMessagePanelOpen
              readOnly={false}
            />
          )}
        </div>
      </Content>
    </ContentContainer>
  );
}
